import { useCallback, useRef, useState } from 'react';

export const useUploadPartOfFile = (): [
  (
    url: URL | string,
    data: Blob | File | FormData,
    options?: { method?: string; timeout?: number; headers?: [string, string][] }
  ) => Promise<string | undefined>,
  {
    error?: Error;
    uploading: boolean;
    abort: (() => void) | undefined;
    progress: { value: number; total: number; percentage: number };
  }
] => {
  const [progress, setProgress] = useState({ value: 0, total: 0, percentage: 0 });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<Error>();
  const abortRef = useRef<() => void>();

  return [
    useCallback(
      async (url, data, options) =>
        new Promise<string | undefined>((resolve, reject) => {
          try {
            const xhr = new XMLHttpRequest();
            let loadEnded = false;

            abortRef.current = (): void => {
              xhr.abort();
            };

            if (options?.timeout) {
              xhr.timeout = options.timeout;
            }

            xhr.upload.addEventListener('loadstart', (event) => {
              setError(undefined);
              setUploading(true);
              setProgress({ value: 0, total: event.total, percentage: 0 });
            });

            xhr.upload.addEventListener('progress', (event) => {
              setProgress({
                total: event.total,
                value: event.loaded,
                percentage: (event.loaded / event.total) * 100 // %
              });
            });

            xhr.upload.addEventListener('loadend', (event) => {
              setProgress((prev) => ({
                ...prev,
                value: event.loaded,
                percentage: event.loaded === event.total ? 100 : (event.loaded / event.total) * 100
              }));

              setUploading(false);
              loadEnded = true;
            });

            const errorAction = (event: Event): void => {
              const error_ = new Error(`Upload failed: ${xhr.statusText || event.type}`);

              setUploading(false);
              setError(error_);
              reject(error_);
            };

            xhr.upload.addEventListener('error', errorAction);
            xhr.upload.addEventListener('abort', errorAction);
            xhr.upload.addEventListener('timeout', errorAction);

            xhr.open(options?.method ?? 'PUT', url, true);

            options?.headers?.forEach(([name, value]) => {
              xhr.setRequestHeader(name, value);
            });

            xhr.addEventListener('readystatechange', () => {
              if (xhr.readyState === XMLHttpRequest.DONE) {
                const { status } = xhr;

                if (loadEnded && (status === 0 || (status >= 200 && status < 400))) {
                  const etag = xhr.getResponseHeader('ETag');

                  resolve(etag ?? undefined);
                } else {
                  xhr.upload.dispatchEvent(new Event('error'));
                }
              }
            });

            xhr.send(data);
          } catch (error_: unknown) {
            const e = <Error>error_;

            setError(e);
            reject(e);
          }
        }),
      []
    ),
    {
      progress,
      uploading,
      error,
      abort: abortRef.current
    }
  ];
};
